import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_KEY = "pwa_install_dismissed_at";
const REGISTERED_KEY = "pwa_install_registered";
const DISMISS_DAYS = 7;

function detectPlatform(): "android" | "ios" | "other" {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return "other";
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // @ts-expect-error iOS Safari
    window.navigator.standalone === true
  );
}

async function registerInstall(platform: "android" | "ios" | "other") {
  try {
    if (localStorage.getItem(REGISTERED_KEY) === "1") return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("pwa_installs" as any).insert({
      user_id: user?.id ?? null,
      platform,
      user_agent: navigator.userAgent,
    });
    localStorage.setItem(REGISTERED_KEY, "1");
  } catch (err) {
    console.warn("PWA install tracking failed", err);
  }
}

export function PWAInstallPrompt() {
  const isMobile = useIsMobile();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const platform = typeof window !== "undefined" ? detectPlatform() : "other";

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Se já está instalado (standalone), registra caso ainda não tenha
    if (isStandalone()) {
      registerInstall(platform);
      return;
    }

    // Ainda não dispensou recentemente?
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    if (!isMobile) return;

    // Android/Chrome: espera beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // App instalado (Chrome dispara appinstalled)
    const installedHandler = () => {
      registerInstall(platform);
      setVisible(false);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS: sem evento; mostra banner com instruções após pequeno delay
    let iosTimer: number | undefined;
    if (platform === "ios") {
      iosTimer = window.setTimeout(() => setVisible(true), 2000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [isMobile, platform]);

  const handleInstall = async () => {
    if (platform === "ios") {
      setShowIOSInstructions(true);
      return;
    }
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      registerInstall(platform);
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSInstructions(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 md:hidden pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-md rounded-xl border bg-background/95 backdrop-blur shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Instalar Data Hire Club</p>
            {showIOSInstructions ? (
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Toque em <Share className="inline h-3 w-3 mx-0.5" /> <strong>Compartilhar</strong> e depois em <strong>“Adicionar à Tela de Início”</strong>.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                Acesse vagas e a comunidade direto do seu celular, como um app.
              </p>
            )}
            <div className="flex gap-2 mt-3">
              {!showIOSInstructions && (
                <Button size="sm" onClick={handleInstall} className="h-8 px-3 text-xs">
                  Instalar
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="h-8 px-3 text-xs"
              >
                {showIOSInstructions ? "Fechar" : "Agora não"}
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
